'use strict';

import { flow, observable, configure, action, makeObservable } from "mobx";
configure({enforceActions: 'always'});
import {PersonFill, Google, Facebook} from "react-bootstrap-icons";
import ReactGA from 'react-ga4';
import moment from "moment";

import {WashingtonW} from "./CustomIcons";
import {
    DEFAULT_ANALYTICS_BUFFER_SIZE,
    AUTH_FLOW_DISPLAY_LOGIN,
    AUTH_FLOW_PROCEED_TO_IDP,
    IdentityProviders,
    UserRoles,
    States,
    UpdateableUserAttributes,
    headers as HEADERS
} from './AppConstants';
import {
    processGetResponseBody,
    processDeleteResponse,
    processDeleteResponseBody
} from './FetchUtils';
import ExceptionUtils from './ExceptionUtils';
import BaseAnalyticsStore from './BaseAnalyticsStore';
import HttpClient from './HttpClient';
import WsClient from "./WsClient";
import Logger from './Logger';
import events from './Events';
import MockTermConstants from './MockTermConstants';
import {isFalse, isTrue, isTrueIgnoreCase, isEmpty, v} from './Utils'
import NowDateTimeEvents from './NowDateTimeEvents';
import WindowPositionStore from './WindowPositionStore';
import UrlPersistenceStore from "./UrlPersistenceStore";

/**
 * The application store/context.
 */
class AppStore {

    state = States.initial;
    newStartTime = 0;

    /**
     * The constructor.
     */
    constructor() {
        makeObservable(this, {
            state: observable,
            newStartTime: observable,
            doLogin: action,
            updateNewStartTime: action,
            setMaintenanceMode: action,
            _done: action
        });

        this.appContext = "unset";
        this.session = {};

        // Page context registry storage. See notes below.
        this.pageContext = undefined;

        // "Now" stuff.
        this.now = {};
        this.now.momentNewStartTimeWasSet = 0;
        this.now.mockCurrentTerm = "";
        this.now.mockPlannableTerms = [];

        // Go ahead and instantiate these. However, some may need further configuration once the session object comes back.
        // Do that in _initialize().
        this.exceptionUtils = new ExceptionUtils(this);
        this.httpClient = new HttpClient(this, this.getExceptionUtils());
        this.analyticsStore = new BaseAnalyticsStore(this, this.httpClient);
        this.logger = new Logger(this.analyticsStore);
        this.urlPersistenceStore = new UrlPersistenceStore(this.logger, this.analyticsStore);
        this.windowPositionStore = new WindowPositionStore(this.urlPersistenceStore);
    }

    /**
     * Initialize common components.
     * @private
     */
    _initialize(session) {
        // Setup websockets if configured. This should only happen after the session is established.
        if (this.isWebSocketsEnabled()) {
            const url = this.getWebSocketsUrl();
            this.wsClient = new WsClient(this, url, () => {
                // This callback will be executed when/if the websockets connection is established. The approach
                // is necessary to avoid a race condition where a request for data can happend before the socket
                // is established ... causing the request to be executed synchronously.
                this.getLogger().debug("AppStore got callback indicating that a websocket has been established.");
                // Unblock ...
                this._done();
            });
        }
        // initializes the Google Analytics tracking code for all apps which use uw-react-common.App (Tracking ID comes
        // from common.properties) as well as our internal analytics.
        if ( this.getGoogleAnalyticsTrackingId() && this.isAnalyticsEnabled() ) {
            ReactGA.initialize(this.getGoogleAnalyticsTrackingId());
        }
        // Configure the analytics store.
        this.getAnalyticsStore().setDebug(this.isDebug());
        this.getAnalyticsStore().setBufferSize(this.getAnalyticsBufferSize())
        this.windowPositionStore.setDebug(this.isDebug());
        this.urlPersistenceStore.setDebug(this.isDebug());
    }

    /**
     * Initialize the application by fetching session data and whatnot.
     *
     * @param name The application name. This method can be called after the initial call, so the it's optional, but
     * if it isn't set the app won't behave correctly.
     */
    mount = flow(function* (name) {
        if (name) {
            this.appContext = name;
        }

        // Start the window position store.
        this.windowPositionStore.mount();

        let url = "api/session";

        /**
         * Call the session service. Successful responses will contain a session object.
         * If we are unauthenticated the response will be a 401 with a body that will determine whether to render our
         * login page, or go straight to the netid login page.
         */
        try {
            this.state = States.pending;

            /**
             * This request goes to the origin which gets redirected to the application API.
             */
            let response = yield fetch(url, {credentials: "include"});

            if (response.status === 503) {
                this.apiUrl = this._extractHostname(response.url);
                this.state = States.maintenance
                return;
            }

            // If 403 Forbidden, the user is authenticated but not allowed to access this app
            if (response.status === 403) {
                this.state = States.unauthorized;
                return;
            }

            let responseBody = processGetResponseBody(yield response.text());

            // If unauthorized, start a login experience and short ciruit
            if (response.status === 401) {
                /*
                 * response.url contains the URL that the redirect bucket redirected to, so parse and save it.
                 * We'll need this when we call login because the CloudFront redirect bucket can only handle GET
                 * and HEAD methods.
                 */
                this.apiUrl = this._extractHostname(response.url);

                if (responseBody.next === AUTH_FLOW_PROCEED_TO_IDP) {
                    // We should send the user directly to the netid idp
                    this.login(IdentityProviders.netId.id);
                } else if (responseBody.next === AUTH_FLOW_DISPLAY_LOGIN) {
                    // We should display the login page
                    this.doLogin();
                } else {
                    // Something's not right but we should display the login page anyways.
                    this.doLogin();
                }
                return;
            }

            if (! response.ok) {
                // Couldn't get a session so log an error.
                this.getLogger().error("Session request failed.",
                    {responseStatus: response.status, responseText: response.statusText});
                throw response
            }

            this.session = responseBody;
            this.apiUrl = this.session.application.api_url;

            // Do the final initialization ...
            this._initialize();

            // If websockets isn't enabled then go ahead and set the state to done. Otherwise, WsClient will call
            // _done() once a socket is established or a timeout is reached.
            if (! this.isWebSocketsEnabled()) {
                this._done();
            }
        } catch (error) {
            /*
             * Let exceptionUtils handle this. It's possible that a session hasn't been established yet and that the
             * error won't get relayed to the backedn, but it will get logged like other exceptions.
             */
            this.exceptionUtils.handleException(error, "get_session");
            this.errorMessage = this.GENERIC_ERROR_MESSAGE;
            this.state = States.error;
        }
    }.bind(this));

    /**
     * This MobX action method sets the state variable to done. Added this to accommodate waiting for a websockets open
     * event when websockets is enabled.
     * @private
     */
    _done() {
        this.state = States.done;
    }

    _extractHostname(url) {
        let hostname;
        // Remove protocol (http, ftp, etc.) and get hostname
        if (url.indexOf("//") > -1) {
            hostname = url.split('/')[2];
        } else {
            hostname = url.split('/')[0];
        }

        // Remove port number
        hostname = hostname.split(':')[0];
        // Remove "?"
        hostname = hostname.split('?')[0];

        if (url.startsWith("https")) {
           hostname = "https://" + hostname;
        } else {
           hostname = "http://" + hostname;
        }

        return hostname;
    };

    /**
     * Start the login process.
     *
     * This function sends a GET to the /api/login endpoint, and navigates to
     * the returned URL.  The returned URL will be the shibboleth URL that the
     * user needs to nav to for authentication.
     */
    login = flow(function* (type) {
        // This a generator function!
        this.state = States.pending;
        try {
            const url = `${this.getApplicationApiUrl()}/api/login?type=${type}`;
            let response = yield fetch(url,
                {
                    method: 'GET',
                    headers: {
                        // Send our current location so we can be sent back here after auth
                        [HEADERS.relaystate]: window.location.href,
                    }
                });

            let responseBody = processGetResponseBody(yield response.text());

            // navigate to the returned URL
            window.location = responseBody.url;

        } catch (error) {
            if (error === 401 || error.toLowerCase() === "unauthorized") {
                this.state = States.unauthenticated;
            } else {
                console.error(error);
                this.state = States.error;
            }
        }
    }.bind(this));

    /**
     * Performs a logout and refreshes the page.
     * 
     * Optionally takes a callback that will be run instead of refreshing the page if, for example, you want to redirect
     * somewhere else after logging out. The callback should set AppStore's state to Done as necessary.
     */
    logout = flow(function* (callback) {
        this.state = States.pending;
        try {
            let url = `${this.getApplicationApiUrl()}/api/login`;
            yield fetch(url, {
                method: 'DELETE',
                credentials: "include",
                headers: {
                    [HEADERS.csrf]: this.getCSRFToken(),
                },
            })
                .then(processDeleteResponse)
                .then(response => response.text())
                .then(processDeleteResponseBody);
            // If a callback was given, run it - otherwise refresh the page to show the disco page
            if (callback) {
                callback();
            } else {
                this.doRefresh();
            }
        } catch (error) {
            switch (error) {
                case 200:
                    // Check for a success short-circuit.
                    //
                    // If a callback was given, run it.
                    //
                    // Otherwise "reboot" the app. If the app supports guest sessions, one will be created automatically. If the
                    // app doesn't, the user will start in the "unauthenticated" state.
                    if (callback) {
                        callback();
                    } else {
                        this.doRefresh();
                    }
                    break;
                case 401:
                    // Check for a redirect (session timeout etc)
                    this.state = States.done;
                    break;
                default:
                    // Pass anything else to exception utils.
                    this.exceptionUtils.handleException(error, "logout");
                    this.state = States.error;
            }
        }
    }.bind(this));

    /**
     * Force the login component to be displayed, or relogin if possible.
     */
    doLogin() {
        this.state = States.unauthenticated;
        // If the session has expired, we can streamline the login process by sending them right back to the idp
        // they used to login last time, skipping the login page.
        if (this.session && this.session.user && this.session.user.identityProvider &&
                this.session.user.identityProvider !== IdentityProviders.unAuthenticated.id) {
            this.login(this.session.user.identityProvider);
        }
    }

    /**
     * Forces the UI to refresh and pull down the latest version of the code.
     */
    doRefresh() {
        window.location.reload();
    }

    /**
     * Gets the application API URL from the session.
     * @return Return the URL if the store has been initialized.
     */
    getApplicationApiUrl() {
        return this.apiUrl;
    }

    /**
     * Redirect the browser page to the given url
     */
    redirect(url) {
        window.location.href = url
    }

    /**
     * Returns the path part of the URL in the URL bar. This is often used to pass context back to the server where
     * it is put on the session to be read by the LoginRedirectController.
     *
     * @return {string} An encoded version of the current URL.
     */
    getCurrentPath() {
        let encodedUri = encodeURIComponent(this.getRawPathname()).replace("#", "%23");
        return encodedUri;
    }

    getRawPathname() {
        const appContextWithSlash = `/${this.appContext}`;
        let currentHref = window.location.href;

        // the path is everything in the url that includes the app context and everything after it
        // but the appContext could be "plan", and the first part of the currentHref could have "myplan" ,
        // so when finding where to build the substring, use the app context with a leading slash,
        // then add a 1 to to that index so the result does not include the leading slash
        return currentHref.substring(currentHref.indexOf(appContextWithSlash) + 1);
    }

    /**
     * This is for unit tests.
     */
    setSession(session) {
        this.session = session;
    }

    /**
     * Sets the current page context, providing a way for components to discover their context when they are generating
     * analytics events. Another, maybe better, approach would be to pass the context in via props. Not changing it today though.
     * @param pageName The context string. Usually, the page. See the logs for examples.
     */
    registerPageContext(pageName) {
        this.pageContext = pageName;
    }

    /**
     * Resets the page context. Usually called when a component unmounts.
     */
    unRegisterPageContext() {
        this.pageContext = undefined;
    }

    /**
     * Gets the current page context. Typically used to construct analytics events.
     */
    getPageContext() {
        return this.pageContext ? this.pageContext : "";
    }

    /**
     * Gets the value of the given configuration item.
     * @param key The configuration key to look for.
     * @return {*} Returns the value or undefined.
     */
    getConfigValue(key) {
        return this.session?.application?.[key];
    }

    getAppContext() {
        return this.appContext;
    }

    getEnvironment() {
        return this.getConfigValue("environment");
    }

    /**
     * These following methods make up the session API that components in an application can use.
     */
    getUserName() {
        return this.session?.user?.userName;
    }

    getDisplayName() {
        return this.session.user.displayName;
    }

    getStudentNumber() {
        return this.session.user.studentNumber;
    }

    getStudentSystemKey() {
        return this.session.user.studentSystemKey;
    }

    getUserEmail() {
        return this.session.user.email;
    }

    /*
     * Gets a preference by key.
     * TODO: UNTESTED.
     */
    getPreference(key) {
        return this.session.user.preferences[key];
    }

    /**
     * Reads the given attribute from session application attribute object
     * @param attribute of the property to be read
     * @returns the value of the property if set else undefined
     */
    getAppAttribute(key) {
        return v(key, this.session.application);
    }

    /**
     * Reads the given attribute from session user attribute object
     * @param attribute of the property to be read
     * @returns the value of the property if set else undefined
     */
    getUserAttribute(attribute) {
        return v(attribute, this.session.user.attributes);
    }

    /**
     * Sets the value of an attribute on the session user attribute object.
     * This is potentially a dangerous operation, so only certain properties are allowed to be set.
     *
     * @param key the attribute to be set
     * @param newValue the new value to be set
     * @return true if the new value of the key was different than any existing value before it was set
     * @throws an exception if the property is not allowed to be set
     */
    updateUserAttribute(key, newValue) {
        if (!UpdateableUserAttributes.includes(key)) {
            throw `Invalid attempt to update immutable user attribute ${key} `;
        }

        if (this.getUserAttribute(key) === newValue) {
            return false;
        }

        this.session.user.attributes[key] = newValue;
        return true;
    }

    getApplicationVersion() {
        return this.session?.application?.["version"];
    }


    isBackdoorAllowed() {
        return isTrue(this.session?.application?.["backdoor_access"]) && isFalse(this.isSocialUser());
    }

    getAnalyticsBufferSize() {
        let bufferSize = DEFAULT_ANALYTICS_BUFFER_SIZE;
        if (this?.session?.application?.analytics_buffer_size) {
            bufferSize = this.session.application.analytics_buffer_size;
        }
        return bufferSize;
    }

    getSessionId() {
        return this.session.sessionId;
    }

    getMyPlanContext() {
        return this.session.application["myplan_context"];
    }

    getMyPlanHostName() {
        return this.session.application["myplan_hostname"];
    }


    getMyPlanUrl() {
        return this.session.application["myplan_hostname"] + "/"
            + this.session.application["myplan_context"] + "/myplan/"
    }

    getNotifyUrl() {
        return this.session.application["notify_url"];
    }

    getPivotHostName() {
        return this.session.application["pivot_hostname"];
    }

    getPreReqMapUrl() {
        return this.session.application["prereqmap_url"];
    }

    getDefaultConsoleTabs() {
        return this.session.application["default_console_tabs"];
    }
    getGoogleAnalyticsTrackingId() {
        let gaProfile;

        if (this.session && this.session.application) {

            gaProfile = this.session.application["ga_profile_student_id"];

            if (this.isAdviser()) {
                gaProfile = this.session.application["ga_profile_adviser_id"];
            }
        }
        else {
            console.log("AppStore: Caller asking for analytics tracking id before session is established, returning undefined");
        }
        return gaProfile;
    }

    /**
     * Returns the hostname of the app server the session is on.
     */
    getHostname() {
        return "N/A";
    }

    /**
     * Returns the build number.
     */
    getChecksum() {
        return this.session?.application?.["checksum"];
    }

    /**
     * @return {string|undefined}
     */
    getCSRFToken() {
        return this.session?.["csrf"];
    }

    /**
     * Returns the name of the application.
     */
    getApplicationName() {
        return this.session?.application?.["name"];
    }

    /**
     * True to get verbose logging.
     */
    isDebug() {
        const loggingLevel = this.session?.application?.["logging_level"];
        if (loggingLevel &&
            (loggingLevel.toLowerCase() === "debug" || loggingLevel.toLowerCase() === "trace")) {
            return true;
        }
        return false;
    }

    /**
     * Returns if devMode is enabled
     */
    isDevMode() {
        // Default to false if the session hasn't been set yet
        if (!this.session || !this.session.application || !this.session.application.dev_mode) {
            return false;
        }

        // app.dev_mode returns a string of "true" so utils isTrue method to make boolean
        return isTrueIgnoreCase(this.session.application.dev_mode);
    }

    /**
     * Returns if analytics is enabled
     */
    isAnalyticsEnabled() {
        // Default to false if the session hasn't been set yet
        if (!this.session || !this.session.application || !this.session.application.analytics_enabled) {
            return false;
        }

        // Use isTrueIgnoreCase to convert the string to a boolean.
        return isTrueIgnoreCase(this.session.application.analytics_enabled);
    }

    /**
     * Returns true if the application is configured for WebSockets.
     * @return {boolean}
     */
    isWebSocketsEnabled() {
        return !!this?.session?.application?.webSocketsUrl;
    }

    /**
     * Returns the configured WebSockets URL if defined. Otherwise, undefined.
     * @return {string}
     */
    getWebSocketsUrl() {
        return this?.session?.application?.webSocketsUrl;
    }

    /**
     * Getter for the WSClient object.
     * @return {WsClient} The WebSocket wrapper object.
     */
    getWebSocketsClient() {
        return this?.wsClient;
    }

    /**
     * The amount of time to wait for a WebSockets connection to be established
     * @return {number} The number of milliseconds.
     */
    getWebSocketsTimeout() {
        return this?.session?.application?.webSocketsTimeout;
    }

    /**
     * The amount of time to wait for an asynchronous request to complete before timeing out
     * @return {number} The number of milliseconds.
     */
    getWebSocketsRequestTimeout() {
        return this?.session?.application?.webSocketsRequestTimeout;
    }

    /**
     * Returns true if the user has one or more of the given roles.
     * @param roles A role or an array of roles. like STUDENT or NON-STUDENT or washington.edu or google or facebook
     * @returns true if the user has one or more of the roles. Otherwise, false.
     */
    _hasRole(roles) {
        let result = false;

        // if something in the path to the roles doesn't exist, don't look in it for anything
        if (!isEmpty(this.session) && !isEmpty(this.session.user) && !isEmpty(this.session.user.roles)) {

            let r = [];
            if (! Array.isArray(roles)) {
                r.push(roles);
            } else {
                r.concat(roles);
            }

            let diff = r.filter(value => this.session.user.roles.includes(value));
            if (diff.length > 0) {
                result = true;
            }
        }

        return result;
    }

    /**
     * Check if the user has any of the given roles.
     * @param roles can be a simple string, a comma separated Roles like ROLE1,ROLE2, or an array.
     * @returns {boolean} true if at least one of the roles matches. Otherwise, false.
     */
    hasRoles(roles) {
        let authorized = false;
        if (!roles) {
            return authorized;
        }

        let r = [];
        if (!Array.isArray(roles)) {
            // Remove whitespace and split into tokens.
            let rolesArray = roles.replace(/\s/g, "").split(",");
            r = r.concat(rolesArray);
        } else {
            r = r.concat(roles);
        }

        for (const role of r) {
            if (authorized) {
                // Short circuit if there's a match.
                break;
            }

            authorized = this._hasRole(role);
        }
        return authorized;
    }


    /**
     * True if the user is a UW student. Otherwise, false.
     */
    isStudent() {
        return this._hasRole(UserRoles.student);
    }

    /**
     * True if the user has the grad student role. Otherwise, false.
     *
     * This role is derived from the degree level property on the user's major(s) value(s) in SWS/SDB.
     */
    isGradStudent() {
        return this._hasRole(UserRoles.gradStudent);
    }

    /**
     * True if the user has the 'professional' role. Otherwise, false.
     *
     * This role is derived from the degree level property on the user's major(s) value(s) in SWS/SDB.
     * There are only 3 or professional programs, but they get their own tab in audit.
     */
    isProfessional() {
        return this._hasRole(UserRoles.professional);
    }

    /**
     * True if user is being advised by an adviser. Otherwise, false.
     */
    isAdviser() {
        return this.session.sessionType.toLowerCase() === "adviser";
    }

    /**
     * Returns the adviser reg Id
     * @returns {*}
     */
    getAdviserId() {
        return this.session.parentUser.regId;
    }

    /**
     * True if the user is authenticated in some way. Otherwise, false.
     */
    isAuthenticated() {
        //  Unauthenticated users have the role "GUEST", but double-checking the "authenticated" roles anyway.
        if (this._hasRole(UserRoles.guest) &&
            IdentityProviders.unAuthenticated.id === this.session.user.identityProvider) {
            return false;
        }
        return true;
    }

    /**
     * True if the user is logged in with a social media account (e.g. Facebook, Google)
     */
    isSocialUser() {
        return this._hasRole(UserRoles.social);
    }

    /**
     * Returns the name of the authentication provider in user-friendly format.
     */
    getAuthenticationProviderText() {
        let provider = this.session.user.identityProvider;
        let providerText = "Unknown";
        switch (provider) {
            case IdentityProviders.netId.id:
                providerText = IdentityProviders.netId.name;
                break;
            case IdentityProviders.google.id:
                providerText = IdentityProviders.google.name;
                break;
            case IdentityProviders.facebook.id:
                providerText = IdentityProviders.facebook.name;
                break;
            case IdentityProviders.unAuthenticated.id:
                providerText = IdentityProviders.unAuthenticated.name;
                break;
        }
        return providerText;
    }

    /**
     * Returns the Icon name for authentication provider.
     */
    getAuthenticationProviderIcon() {
        let authProviderIcon = undefined;
        if (this.isAuthenticated()) {
            switch (this.session.user.identityProvider) {
                case IdentityProviders.netId.id:
                    authProviderIcon = WashingtonW;
                    break;
                case IdentityProviders.google.id:
                    authProviderIcon = Google;
                    break;
                case IdentityProviders.facebook.id:
                    authProviderIcon = Facebook;
                    break;
            }
        } else {
            authProviderIcon = PersonFill;
        }
        return authProviderIcon;
    }

    /**
     * True if the user has the facebook role. Otherwise, false.
     */
    isFacebookUser() {
        return this.session.user.identityProvider === IdentityProviders.facebook.id;
    }

    /**
     * True if the user has the google role. Otherwise, false.
     */
    isGoogleUser() {
        return this.session.user.identityProvider === IdentityProviders.google.id;
    }

    /**
     * Gets a list of audit codes for the user's major(s).
     *
     * If the user isn't a student or no data exists for whatever reason then an empty list is returned.
     */
    getMajors() {
        let majors = this.getUserAttribute('majors');
        if (typeof majors === 'undefined') {
            majors = [];
        }
        return majors;
    }

    /**
     * Gets a list of audit codes for the user's minor(s).
     *
     * If the user isn't a student or no data exists for whatever reason then an empty list is returned.
     */
    getMinors() {
        let minors = this.getUserAttribute('minors');
        if (typeof minors === 'undefined') {
            minors = [];
        }
        return minors;
    }

    /**
     * Returns a human-readable version of now
     */
    getPrettyPrintedNow() {
        return moment(this.getNow()).format('M/D/YY h:mm a');
    }

    /**
     * Checks to see if cookies are enabled for the browser and returns boolean
     */
    hasCookiesDisabled() {
        let cookiesEnabled = isTrue(navigator.cookieEnabled);

        if (typeof navigator.cookieEnabled == "undefined" && !cookieEnabled) {
            document.cookie = "myplan";
            cookiesEnabled = (document.cookie.indexOf("myplan") !== -1);
        }

        return !cookiesEnabled;
    }

    /**
     * Returns 'now' based on the real 'now' date and time or a 'now' time set during testing
     * This method is used by the NowTime component should not be called directly from other components.
     * An event is generated when the now time is updated and components listening to the event can be re-rendered
     * to use the changed 'now'.
     */
    getNow() {
        if (this.newStartTime === 0) {
            return Date.now();
        } else {
            let nowTime = moment(this.newStartTime);
            let timeSinceSet = moment().valueOf() - this.now.momentNewStartTimeWasSet.valueOf();

            nowTime = nowTime.add(timeSinceSet, 'ms');

            return nowTime.toDate();
        }
    }

    /**
     * With great care, this method can be used to override the value that getNow() returns.
     *
     * This should only be called by tests and back door utilities.
     *
     */
    carefullySetNow(timeToSet) {
        this.now.momentNewStartTimeWasSet = moment();
        this.updateNewStartTime(moment(timeToSet.toLowerCase(), 'MM/DD/YYYY hh:mm a'));

        // If the date being set matches one of the mock term data dates, also set the current and plannable mock terms
        let mockTermInfo = undefined;

        MockTermConstants.MOCK_TERM_SET.map((mockTerm, i) => {
            if (!isEmpty(mockTerm.date) && timeToSet === mockTerm.date) {
                mockTermInfo = mockTerm;
            }
        });

        if (mockTermInfo !== undefined) {
            this.now.mockCurrentTerm = mockTermInfo["current"];
            this.now.mockPlannableTerms = mockTermInfo["plannable"];
        } else {
            // if the date being set is not one in the set, clear out any mock term state
            this.now.mockCurrentTerm = "";
            this.now.mockPlannableTerms = [];
        }
        events.emit(NowDateTimeEvents.setNow, this.newStartTime);
    }

    /**
     * Updates the value for newStartTime which is used to override the value that getNow() returns
     * @param newStartTime datetime
     */
    updateNewStartTime(newStartTime) {
        this.newStartTime = newStartTime;
    }

    /**
     * Returns true if a valid value has been set for the mock current term, and false otherwise
     * If a mock current term is set, there must also be at least one mock current plannable term,
     * so this check can be used for any kind of mock term data
     *
     * @returns {boolean}
     */
    hasMockCurrentTerm() {
        return this.now.mockCurrentTerm !== "";
    }

    getMockCurrentTerm() {
        return this.now.mockCurrentTerm;
    }

    getMockCurrentTermInfo() {
        let result = {};

        if (this.hasMockCurrentTerm()) {
            result = MockTermConstants.MOCK_TERM_PAYLOADS[this.getMockCurrentTerm()];
        }

        return result;
    }

    getMockPlannableTerms() {
        return [].concat(this.now.mockPlannableTerms);
    }

    getMockPlannableTermInfos() {
        return this.getMockTermInfos(this.getMockPlannableTerms());
    }

    getMockTermInfos(termSet) {
        let result = [];
        if (!isEmpty(termSet)) {
            for (let index in termSet) {
                if (termSet.hasOwnProperty(index)) {
                    result.push(MockTermConstants.MOCK_TERM_PAYLOADS[termSet[index]]);
                }
            }
        }

        return result;
    }

    getMockCurrentPlannableTerm() {
        let result = "";
        if (this.hasMockCurrentTerm()) {
            result = this.now.mockPlannableTerms[0];
        }

        return result;
    }

    getMockCurrentPlannableTermInfo() {
        let result = {};

        if (this.hasMockCurrentTerm()) {
            result = MockTermConstants.MOCK_TERM_PAYLOADS[this.getMockCurrentPlannableTerm()];
        }

        return result;
    }

    getMockPublishedTerms() {
        let publishedTerms = [];
        if (this.hasMockCurrentTerm()) {

            // the current term is always published, so if the current term is not part of the plannable terms,
            // add it to the published terms
            if (!this.getMockPlannableTerms().includes(this.getMockCurrentTerm())) {
                publishedTerms.push(this.getMockCurrentTerm());
            }

            publishedTerms = publishedTerms.concat(this.getMockPlannableTerms());
        }

        return publishedTerms;
    }

    getMockPublishedTermInfos() {
        return this.getMockTermInfos(this.getMockPublishedTerms());
    }

    /**
     * Clears the override of a value for getNow, reverting it to return the current clock time
     *
     * Also clears any override for the current and plannable terms and emits an event to let the listener components
     * know that the now time has changed
     */
    clearSetNow() {
        this.now.momentNewStartTimeWasSet = 0;
        this.newStartTime = 0;
        this.now.mockCurrentTerm = "";
        this.now.mockPlannableTerms = [];

        events.emit(NowDateTimeEvents.setNow, this.newStartTime);
    }

    setAnalyticsStore(analyticsStore) {
        this.analyticsStore = analyticsStore;
    }

    getAnalyticsStore() {
        return this.analyticsStore;
    }

    setMaintenanceMode() {
        this.state = States.maintenance;
    }

    getExceptionUtils() {
        return this.exceptionUtils;
    }

    getAnalyticsStore() {
        return this.analyticsStore;
    }

    getHttpClient() {
        return this.httpClient;
    }

    getLogger() {
        return this.logger;
    }

    getUrlPersistenceStore() {
        return this.urlPersistenceStore;
    }
}

export default new AppStore();